/* 3D 생성 계약. 어휘는 정본 dsl.json 의 math3d 생성 명령이다. 모델이 낸 호출 JSON 을 대조하고 3D 실행기로 세운다 */
import { acceptsOf, acceptsText, describe, entriesFor, entryOf, kindLabel, menuLine, schemaOf } from './dsl';
import { allObjects3d, executeOne3d, registryOf3d, resetRegistry3d } from './executor3d';
import type { Math3DApp } from './executor3d';
import { clearScene3d } from '../style3d';
import type { DslEntry, ToolCall, ToolResult } from './types';

/** 모델에게 보이지 않는 명령: 내부 기본 개체, 그림 파일, 슬라이더와 체크박스, 글과 꾸밈, 식의 곡면, 접선(접선의 기하는 아직 검증하지 못했다) */
const HIDDEN = ['object.addDefaultLine', 'object.addDefaultPlane', 'object.addImage', 'object.addSlider', 'object.addCheckbox', 'object.addText3D', 'object.addAlgebraic', 'object.addDecoCallout', 'object.addDecoLength', 'object.addDecoParallel', 'object.addDecoAngle', 'object.addTangentLine'];
/** 그림에서 가려진 모서리를 점선으로 만드는 명령 하나만 속성 변경 명령으로 더한다 */
const STYLE_TOOLS = ['object.setLinearDashed'];
export const PALETTE3D: DslEntry[] = entriesFor('math3d').filter((e) => (e.name.startsWith('object.add') && !HIDDEN.includes(e.name)) || STYLE_TOOLS.includes(e.name));
export const TOOL_NAMES3D: string[] = PALETTE3D.map((e) => e.name);

/** 개체 이름 풀. 점, 선, 면, 입체, 측정이 서로 겹치지 않는다 */
export const NAMES3D: string[] = [
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''), 'P1', 'P2', 'P3', 'Q1', 'Q2', 'A1', 'A2', 'B1', 'B2',
  'l1', 'l2', 'l3', 'l4', 'l5', 'l6', 'l7', 'l8', 'l9', 'l10', 'l11', 'l12', 'l13', 'l14', 'l15', 'l16', 'm1', 'm2', 'n1', 'n2', 'alpha', 'beta', 'gamma', 'pi1', 'C1', 'C2', 'C3', 'S1', 'S2', 'S3', 'T1', 'T2', 'V1', 'V2', 'V3', 'd1', 'd2', 'd3',
];

/** 호출 하나의 모양. tool 은 const, args 는 그 명령의 인자만(전부 required, 선택 인자는 null 허용, 그 밖은 금지) */
function callShape3d(entry: DslEntry): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  for (const p of entry.params) {
    let shape: Record<string, unknown>;
    if (p.type === 'ref') shape = { type: 'string', description: '이미 선 개체의 이름(이 JSON 에서 먼저 세운 name 이나 이미 선 개체의 이름)' };
    else if (p.type.startsWith('ref[')) shape = { type: 'array', items: { type: 'string' }, description: '이미 선 개체의 이름들' };
    else if (p.name === 'name') shape = { type: 'string', enum: NAMES3D };
    else if (p.type === 'number|string') shape = { type: 'number' };
    else shape = schemaOf(p.type);
    properties[p.name] = p.optional ? { anyOf: [shape, { type: 'null' }] } : shape;
  }
  return { type: 'object', properties: { tool: { type: 'string', const: entry.name }, args: { type: 'object', properties, required: Object.keys(properties), additionalProperties: false } }, required: ['tool', 'args'], additionalProperties: false };
}

/** 구성 단계 스키마: calls 배열 하나 */
export function callsSchema3d(tools: readonly string[] = TOOL_NAMES3D): Record<string, unknown> {
  const shapes = tools.map((n) => entryOf(n)).filter((e): e is DslEntry => !!e && e.tool === 'math3d').map(callShape3d);
  return { type: 'object', properties: { calls: { type: 'array', items: shapes.length === 1 ? shapes[0] : { anyOf: shapes }, maxItems: 80 } }, required: ['calls'], additionalProperties: false };
}

/** 계획 단계의 메뉴 줄 */
export function menuText3d(names: readonly string[] = TOOL_NAMES3D): string {
  return names.map((n) => entryOf(n)).filter((e): e is DslEntry => !!e).map(menuLine).join('\n');
}

/** 구성 단계가 받는 사용법. 취합한 명령만 */
export function paletteText3d(names: readonly string[]): string {
  return names.map((n) => entryOf(n)).filter((e): e is DslEntry => !!e).map(describe).join('\n\n');
}

/** 계획의 도구 이름 중 어휘에 있는 것만 */
export function inPalette3d(names: readonly unknown[]): string[] {
  return [...new Set(names.filter((n): n is string => typeof n === 'string' && TOOL_NAMES3D.includes(n)))];
}

/** 모델의 응답을 호출 목록으로 읽는다. 모양이 아닌 항목은 버린다 */
export function readCalls3d(raw: unknown): ToolCall[] {
  const list = raw && typeof raw === 'object' && Array.isArray((raw as { calls?: unknown }).calls) ? (raw as { calls: unknown[] }).calls : [];
  return list.flatMap((one, i) => {
    const o = one as { tool?: unknown; args?: unknown };
    if (!o || typeof o.tool !== 'string' || !o.args || typeof o.args !== 'object') return [];
    return [{ id: `c${i}`, type: 'function' as const, function: { name: o.tool, arguments: JSON.stringify(o.args) } }];
  });
}

/** 이미 선 개체의 이름과 종류. 되먹임과 다음 요청에 상태로 준다 */
export function sceneState3d(app: Math3DApp): string[] {
  const registry = registryOf3d(app);
  return registry.knownNames().map((n) => { const o = registry.objectOf(n); return `- ${n} (${o?.type ?? '?'}${o?.category ? `/${o.category}` : ''})`; });
}

/** 개체를 가리키는 인자가 그 자리의 받는 종류에 맞는지. 어긋나면 사유 문장, 맞으면 빈 글자 */
function kindProblem(app: Math3DApp, entry: DslEntry, args: Record<string, unknown>): string {
  const registry = registryOf3d(app);
  const accepts = acceptsOf(entry);
  for (const p of entry.params) {
    const kinds = accepts.get(p.name);
    const v = args[p.name];
    if (!kinds || v === undefined || v === null) continue;
    const refs = typeof v === 'string' ? [v] : Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    for (const r of refs) {
      const o = registry.get(r).map((id) => allObjects3d(app).find((one) => one.id === id))[0];
      if (o?.type && !kinds.includes(o.type)) return `${entry.name} 의 ${p.name} 에 준 "${r}" 은 ${kindLabel(o.type)} 이다. ${acceptsText(entry)}`;
    }
  }
  return '';
}

export interface Drawn3 {
  rejected: { tool: string; detail: string; name: string }[];
  results: ToolResult[];
  /** 세워진 호출을 세운 순서대로. 다시 세울 때(재생) 그대로 쓴다 */
  executed: ToolCall[];
}

const DEFER_MARK = '은 세워진 개체의 이름이 아니다';

/** 호출 묶음을 대조하고 세운다. 아직 안 선 이름을 가리킨 호출은 미뤄 두었다가 다른 호출이 이름을 세운 뒤 다시 시도한다 */
export async function drawCalls3d(app: Math3DApp, raw: unknown, afterEach?: (names: string[]) => Promise<void>): Promise<Drawn3> {
  const registry = registryOf3d(app);
  const results: ToolResult[] = []; const executed: ToolCall[] = []; const rejected: Drawn3['rejected'] = [];
  const nameOf = (call: ToolCall): string => { try { return String(JSON.parse(call.function.arguments).name ?? ''); } catch { return ''; } };
  let queue = readCalls3d(raw);
  const total = queue.length;
  for (let pass = 0; pass < 4 && queue.length; pass += 1) {
    const later: ToolCall[] = [];
    for (const call of queue) {
      const entry = entryOf(call.function.name);
      let outcome: ToolResult;
      if (!entry || !TOOL_NAMES3D.includes(entry.name)) outcome = { call, ok: false, method: call.function.name, detail: `${call.function.name} 은 이 문항에서 쓸 수 있는 3D 도구가 아니다` };
      else {
        const problem = kindProblem(app, entry, JSON.parse(call.function.arguments) as Record<string, unknown>);
        outcome = problem ? { call, ok: false, method: entry.name, detail: problem } : await executeOne3d(app, call, registry);
      }
      if (!outcome.ok && outcome.detail.includes(DEFER_MARK) && pass < 3) { later.push(call); continue; }
      results.push(outcome);
      if (outcome.ok) { executed.push(call); if (afterEach) await afterEach(registry.knownNames()); } else rejected.push({ tool: call.function.name, detail: outcome.detail, name: nameOf(call) });
    }
    if (later.length === queue.length) { for (const call of later) { const r = await executeOne3d(app, call, registry); results.push(r); if (!r.ok) rejected.push({ tool: call.function.name, detail: r.detail, name: nameOf(call) }); else executed.push(call); } break; }
    queue = later;
  }
  return { rejected, results: results.slice(0, Math.max(total, results.length)), executed };
}

/** 세워진 호출을 앞에서 count 개까지 다시 세운다. 장면을 비우고 처음부터 재생한다(멱등) */
export async function replayCalls3d(app: Math3DApp, calls: ToolCall[], count: number): Promise<void> {
  await clearScene3d(app);
  resetRegistry3d(app);
  const registry = registryOf3d(app);
  for (const call of calls.slice(0, count)) await executeOne3d(app, call, registry);
}
